import { Component, OnInit } from "@angular/core";
import { Router, ActivatedRoute } from "@angular/router";
import { UntypedFormBuilder, UntypedFormGroup, Validators } from "@angular/forms";
import { first } from "rxjs/operators";
import * as _ from "lodash";
import { MsalService } from "@azure/msal-angular";

import { AuthenticationService, UserService, AlertService, StorageService, AdmissionsService, LookupsService, HousingService, OrientationService, PermitToStayService } from "../_services";
import { AppGlobals } from "../app.globals";

@Component({
    templateUrl: "login.component.html",
    styleUrls: ["login.component.css"]
})
export class LoginComponent implements OnInit {
    loginForm: UntypedFormGroup;
    submitted = false;
    returnUrl: string;
    hide = true;

    constructor(private formBuilder: UntypedFormBuilder, private route: ActivatedRoute, private router: Router, private authenticationService: AuthenticationService,
        private userService: UserService, private alertService: AlertService, private appGlobals: AppGlobals, private storageService: StorageService, private permitToStayService: PermitToStayService,
        private admissionsService: AdmissionsService, private lookupsService: LookupsService, private housingService: HousingService, private orientationService: OrientationService, private msalService: MsalService) {
    }

    ngOnInit() {
        this.appGlobals.pageTitleValue = "Login";

        this.loginForm = this.formBuilder.group({
            username: ["", Validators.required],
            password: ["", Validators.required]
        });

        const verificationCode = this.route.snapshot.queryParamMap.get("verificationCode");

        if (verificationCode) {
            this.userService.verify(verificationCode)
                .pipe(first())
                .subscribe(() => {
                    this.alertService.showMessage("Account Verified Successfully.", "Account Verification", this.appGlobals.alertMessageTypes.success);
                    this.router.navigate(["/login"]);
                });
        } else {
            // get return url from route parameters or default to '/'
            this.returnUrl = this.route.snapshot.queryParams["returnUrl"] || "/";

            // redirect to home if already logged in
            if (this.authenticationService.currentUserValue) {
                this.router.navigate([this.returnUrl]);
            }
        }
    }

    get loginFormControls() { return this.loginForm.controls as any; }

    onSubmit() {
        this.submitted = true;

        // stop here if form is invalid
        if (this.loginForm.invalid) {
            return;
        }

        this.authenticationService.login(this.loginFormControls.username.value, this.loginFormControls.password.value, false)
            .pipe(first())
            .subscribe((user: any) => {
                this.login(user);
            });
    }

    windowsLogin() {
        this.submitted = true;

        this.msalService.loginPopup()
            .subscribe({
                next: (result) => {
                    this.authenticationService.login(result.accessToken, null, true)
                        .pipe(first())
                        .subscribe(user => {
                            this.login(user);
                        });
                },
                error: (error) => console.log(error)
            });
    }

    login(user: any) {
        if (user.requiresPasswordChange)
            this.router.navigate(["/resetPassword"]);
        else {
            if (user.studentId) {
                this.router.navigate([this.returnUrl ? this.returnUrl : "/"], { skipLocationChange: true });

                this.userService.syncStudentData(user.studentId)
                    .pipe(first())
                    .subscribe(() => {
                        this.userService.getStudentImage(user.studentCode, user.attachmentId)
                            .pipe(first())
                            .subscribe((data: any) => {
                                user.imageSrc = data.imageSrc;
                                user.profileImageNeedsStudentReview = data.attachmentNeedsStudentReview;

                                let tempStr = {};

                                if ((user.studentCode || user.programCodes)) {
                                    if (!user.imageSrc) {
                                        tempStr = {
                                            code: "picture",
                                            message: "You need to <strong>Upload</strong> a <strong>Profile Picture</strong>",
                                            viewed: false,
                                            dialogBox: "accountProfile"
                                        };

                                        user.userNotifications.push(tempStr);
                                    } else {
                                        if (user.profileImageNeedsStudentReview) {
                                            tempStr = {
                                                code: "reUploadPicture",
                                                message: "You need to <strong>Re-Upload</strong> your <strong>Profile Picture</strong>",
                                                viewed: false,
                                                dialogBox: "accountProfile"
                                            };

                                            user.userNotifications.push(tempStr);
                                        }
                                    }
                                }

                                //get housing programs for current and future semesters
                                this.lookupsService.getHousingProgramsByDate()
                                    .pipe(first())
                                    .subscribe((housingPrograms: any) => {
                                        const programsThatRequireAdmissions = housingPrograms.filter(f => f.requiresAdmissionsApplication);

                                        if (programsThatRequireAdmissions && programsThatRequireAdmissions.length > 0) {
                                            user.hasProgramCodeWithRequiredAdmissions = !!programsThatRequireAdmissions.find(f => {
                                                return user.programCodes && user.programCodes.indexOf(f.program.code) !== -1;
                                            });// != null || user.studentCode;
                                        }

                                        if (!user.programCodes || user.programCodes.length === 0 || user.hasProgramCodeWithRequiredAdmissions) {
                                            user.canApplyForAdmissions = true;

                                            //check to see whether the user has a valid admissions application
                                            this.admissionsService.getApplications(user.id)
                                                .pipe(first())
                                                .subscribe((applications: any) => {
                                                    var redirectUrl = null;

                                                    user.hasAdmissionsApplication = applications && applications.length > 0;

                                                    if (applications && applications.length > 0) {
                                                        let today = new Date();
                                                        today.setHours(0, 0, 0, 0);

                                                        //application status is not applicant, cancelled, rejected, deferred or has been submitted before the application deadline (for ds funnels)
                                                        user.hasValidAdmissionsApplication =
                                                            applications.find(f => f.admissionsApplicationStatusId !== 9 &&
                                                                f.admissionsApplicationStatusId !== 8 &&
                                                                f.admissionsApplicationStatusId !== 12 &&
                                                                f.admissionsApplicationStatusId !== 29 &&
                                                                !f.isCancelled &&
                                                                !(f.notificationDate && new Date(f.notificationDate) > today && f.admissionsApplicationRegistrationStatusDescription.indexOf("Accept") !== -1)) != null;

                                                        //check if user has admissions app for a program
                                                        if (user.hasValidAdmissionsApplication && !user.hasProgramCodeWithRequiredAdmissions) {
                                                            //check to see if we have a program for the student's provider
                                                            user.hasProgramCodeWithRequiredAdmissions = !!housingPrograms.find(f => /*f.isActive &&*/ f.providerId && f.providerId === user.providerId && applications.find(a => a.scholasticPeriodId === f.program.scholasticPeriodId && a.admissionsApplicationStatusId !== 9 && a.admissionsApplicationStatusId !== 8 && a.admissionsApplicationStatusId !== 29 && !a.isCancelled));

                                                            //if (!user.hasProgramCodeWithRequiredAdmissions)
                                                            user.hasAdmissionsForProgram = !!applications.find(f => f.programId || f.additionalProgramId);
                                                            user.admissionsStartDate = (_.minBy(applications.filter(a => a.admissionsApplicationStatusId !== 9 && a.admissionsApplicationStatusId !== 8 && a.admissionsApplicationStatusId !== 29 && !a.isCancelled), "scholasticPeriodStartDate") as any).scholasticPeriodStartDate;
                                                        }

                                                        //show missing documents notification if the user has some missing documents and the registration status is pending, conditional or provisional acceptance 
                                                        //and the admissions status is not deferred, cancelled, admissions rescinded or rejected
                                                        if (applications.find(f => f.numRequiredDocuments > 0 && !f.isCancelled &&
                                                            (f.admissionsApplicationRegistrationStatusId === 6 ||
                                                                f.admissionsApplicationRegistrationStatusId === 7 ||
                                                                f.admissionsApplicationRegistrationStatusId === 16) &&
                                                            (f.admissionsApplicationStatusId !== 6 &&
                                                                f.admissionsApplicationStatusId !== 9 &&
                                                                f.admissionsApplicationStatusId !== 12 &&
                                                                f.admissionsApplicationStatusId !== 29 &&
                                                                f.admissionsApplicationStatusId !== 36 &&
                                                                f.admissionsApplicationStatusId !== 40 &&
                                                                f.admissionsApplicationStatusId !== 14 &&
                                                                f.admissionsApplicationStatusId !== 30 &&
                                                                f.admissionsApplicationStatusId !== 4 &&
                                                                f.admissionsApplicationStatusId !== 5))) {
                                                            tempStr = {
                                                                code: "admissionsApplicationDocuments",
                                                                message: "You need to <strong>Upload Documents</strong> for your <strong>Admissions Application</strong> to be <strong>Completed</strong>.",
                                                                viewed: false,
                                                                url: "/admissions"
                                                            };

                                                            user.userNotifications.push(tempStr);

                                                            redirectUrl = "/admissions";
                                                        }
                                                    }

                                                    if (!user.hasAdmissionsApplication) {
                                                        tempStr = {
                                                            code: "admissionsApplication",
                                                            message: "You have not yet <strong>Completed</strong> your <strong>Admissions Application</strong>.",
                                                            viewed: false,
                                                            url: "/admissions"
                                                        };

                                                        user.userNotifications.push(tempStr);

                                                        redirectUrl = "/admissions";
                                                    }

                                                    user.canApplyForHousing = (user.imageSrc !== null && user.hasValidAdmissionsApplication && user.studentCode !== null);

                                                    this.storageService.store(this.appGlobals.localStorageKeys.currentUser, user);

                                                    if (redirectUrl)
                                                        this.router.navigate([redirectUrl], { skipLocationChange: true });
                                                    //else {

                                                    if (user.hasValidAdmissionsApplication && user.studentId) {
                                                        this.orientationService.getStudentOrientationSemesters(user.studentId)
                                                            .pipe(first())
                                                            .subscribe((orientations: any) => {
                                                                const today = new Date();

                                                                if (orientations && orientations.length > 0 && orientations.find(f => new Date(f.orientationSemesterActivationDate) <= today && f.orientationSemesterStudentStatusId !== 4)) {
                                                                    if (!user.hasEmergencyContacts || !user.hasIdentityDocumentInfo || !user.imageSrc) {
                                                                        if (!user.hasEmergencyContacts) {
                                                                            tempStr = {
                                                                                code: "emergencyContacts",
                                                                                message: "You need to <strong>Provide</strong> your <strong>Emergency Contact</strong> Details",
                                                                                viewed: false,
                                                                                dialogBox: "accountProfile"
                                                                            };

                                                                            user.userNotifications.push(tempStr);
                                                                        }

                                                                        if (!user.hasIdentityDocumentInfo) {
                                                                            tempStr = {
                                                                                code: "identityDocumentInfo",
                                                                                message: "You need to <strong>Provide</strong> your <strong>Identity Document</strong> Details",
                                                                                viewed: false,
                                                                                dialogBox: "accountProfile"
                                                                            };

                                                                            user.userNotifications.push(tempStr);
                                                                        }
                                                                    } else {
                                                                        if (orientations.find(f => f.numRequiredDocuments > 0 && f.canBeModified)) {
                                                                            tempStr = {
                                                                                code: "orientationSemesterStudent",
                                                                                message: "You have not yet <strong>Completed</strong> the <strong>Orientation</strong> Process.",
                                                                                viewed: false,
                                                                                url: "/orientation"
                                                                            };

                                                                            user.userNotifications.push(tempStr);

                                                                            redirectUrl = "/orientation";
                                                                        }
                                                                    }

                                                                    if (user.identityDocumentInformationNeedsStudentReview) {
                                                                        tempStr = {
                                                                            code: "identityDocumentInfoNeedsReview",
                                                                            message: "You need to <strong>Review</strong> your <strong>Identity Document</strong> Details",
                                                                            viewed: false,
                                                                            dialogBox: "accountProfile"
                                                                        };

                                                                        user.userNotifications.push(tempStr);
                                                                    }

                                                                    if (orientations.find(f => f.requiresPreArrivalFormResubmission && f.age >= 18)) {
                                                                        tempStr = {
                                                                            code: "requiresPreArrivalFormResubmission",
                                                                            message: "You need to <strong>Resubmit</strong> your <strong>Pre Arrival Form</strong>",
                                                                            viewed: false,
                                                                            url: "/orientation"
                                                                        };

                                                                        user.userNotifications.push(tempStr);

                                                                        redirectUrl = "/orientation";
                                                                    }

                                                                    user.hasValidOrientation = orientations?.length > 0;
                                                                    user.canModifyOrientation = orientations?.length > 0 && orientations.find(f => f.numRequiredDocuments > 0 && f.canBeModified);
                                                                    user.canViewOrientation = orientations?.length > 0 && user.imageSrc && user.hasIdentityDocumentInfo && user.hasEmergencyContacts;

                                                                    this.storageService.store(this.appGlobals.localStorageKeys.currentUser, user);

                                                                    if (redirectUrl)
                                                                        this.router.navigate([redirectUrl], { skipLocationChange: true });
                                                                }

                                                                this.permitToStayService.getStudentOrientationPermitsToStay(user.studentId)
                                                                    .pipe(first())
                                                                    .subscribe((permitsToStay: any) => {
                                                                        if (permitsToStay.find(f => f.orientationPermitToStayStudentStatusId === 7) && permitsToStay.find(f => f.orientationPermitToStayStudentStatusId <= 3)) {
                                                                            if (!user.userNotifications.find(f => f.code === "identityDocumentInfoNeedsReview") && user.identityDocumentInfoExpired) {
                                                                                tempStr = {
                                                                                    code: "identityDocumentInfoExpired",
                                                                                    message: "You need to <strong>Update</strong> your <strong>Identity Document</strong> Details",
                                                                                    viewed: false,
                                                                                    dialogBox: "accountProfile"
                                                                                };

                                                                                user.userNotifications.push(tempStr);
                                                                            }

                                                                            user.identityDocumentInformationNeedsReview = true;
                                                                        }

                                                                        if (permitsToStay.find(f => f.numRequiredDocuments > 0 && f.canBeModified)) {
                                                                            tempStr = {
                                                                                code: "orientationPermitToStayStudent",
                                                                                message: "You have not yet <strong>Completed</strong> the <strong>Immigration</strong> Process.",
                                                                                viewed: false,
                                                                                url: "/immigration"
                                                                            };

                                                                            user.userNotifications.push(tempStr);

                                                                            if (!user.userNotifications.find(f => f.code === "orientationSemesterStudent" || f.code === "requiresPreArrivalFormResubmission"))
                                                                                redirectUrl = "/immigration";
                                                                        }

                                                                        //do not show immigration to ignored students
                                                                        user.canViewImmigration = !!permitsToStay?.find(f => f.orientationPermitToStayStudentStatusId !== 6);

                                                                        this.storageService.store(this.appGlobals.localStorageKeys.currentUser, user);

                                                                        if (redirectUrl)
                                                                            this.router.navigate([redirectUrl], { skipLocationChange: true });
                                                                    });
                                                            });
                                                    }
                                                    //}
                                                });
                                        } else {
                                            user.hasValidProgramCode = user.programCodes && user.programCodes.length > 0;
                                            this.storageService.store(this.appGlobals.localStorageKeys.currentUser, user);

                                            //if we have a valid program code and an image has been uploaded, then allow to submit housing app
                                            if (user.hasValidProgramCode && user.imageSrc) {
                                                this.housingService.getApplications(user.id)
                                                    .pipe(first())
                                                    .subscribe((applications: any) => {
                                                        var redirectUrl = null;

                                                        if (!applications || applications.length === 0 || applications.find(f => !f.dateCompleted && f.housingStudentApplicationStatusId !== 3)) {
                                                            tempStr = {
                                                                code: "housingApplication",
                                                                message: "You have not yet <strong>Completed</strong> your <strong>Housing Application</strong>.",
                                                                viewed: false,
                                                                url: "/housing"
                                                            };

                                                            user.userNotifications.push(tempStr);

                                                            redirectUrl = "/housing";
                                                        }

                                                        user.canApplyForHousing = true;
                                                        user.canApplyForAdmissions = false;

                                                        this.storageService.store(this.appGlobals.localStorageKeys.currentUser, user);

                                                        if (redirectUrl)
                                                            this.router.navigate([redirectUrl], { skipLocationChange: true });
                                                    });
                                            }
                                        }
                                    });
                            });
                    });
            } else {
                const tempStr = {
                    code: "admissionsApplication",
                    message: "You have not yet <strong>Completed</strong> your <strong>Admissions Application</strong>.",
                    viewed: false,
                    url: "/admissions"
                };

                user.canApplyForAdmissions = true;
                user.userNotifications.push(tempStr);

                this.storageService.store(this.appGlobals.localStorageKeys.currentUser, user);

                this.router.navigate(["/admissions"], { skipLocationChange: true });
            }
        }
    }
}
